/*
 * ObservationEdit — issue #707 Stage 1 RHF rewrite.
 *
 * Converted from plain useState to react-hook-form + FormProvider so the
 * shared step components from components/forms/observation/ (AutofocusStep,
 * GuidingStep, ConstraintsStep) can be embedded directly. Hydration and
 * submission both go through the hooks shipped in the first half of #707
 * (useObservationFormDefaults, useObservationSubmit), giving Edit and Submit
 * a single source of truth for the schema↔form mapping.
 *
 * Sexagesimal RA/Dec input is preserved via local display state + Controllers;
 * the canonical decimal values live in the RHF state.
 *
 * Transit-window recalculation logic that used to live here has moved to
 * useObservationSubmit and is unit-tested there.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useObservationFormDefaults } from '../../components/forms/observation/hooks/useObservationFormDefaults';
import { useObservationSubmit } from '../../components/forms/observation/hooks/useObservationSubmit';
import { useDarkTimeWarning } from '../../components/forms/observation/hooks/useDarkTimeWarning';
import DarkTimeWarningAlert from '../../components/observations/DarkTimeWarningAlert';
import { warningMessages } from '../../utils/darkTimeWarning';
import AutofocusStep from '../../components/forms/observation/AutofocusStep';
import GuidingStep from '../../components/forms/observation/GuidingStep';
import ConstraintsStep from '../../components/forms/observation/ConstraintsStep';
import RepetitionStep from '../../components/forms/observation/RepetitionStep';
import UnifiedTargetStep from '../../components/forms/observation/UnifiedTargetStep';
import { createFilterOptions } from '../../utils/filterOptions';
import { isObservationEditable } from '../../utils/observationEditable';

const ObservationEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // #1119: dark-time advisories returned by the PUT. Non-empty holds navigation until acknowledged.
  const [darkTimeWarnings, setDarkTimeWarnings] = useState([]);
  const [observation, setObservation] = useState(null);
  const [observatories, setObservatories] = useState([]);
  const [projects, setProjects] = useState([]);
  // Gate the form render until AFTER RHF has applied the loaded observation via
  // its reactive `values` prop. React runs child passive effects before parent
  // effects, so the step components' mount-only "ensure at least one
  // target/exposure" effects (UnifiedTargetStep / TargetExposureConfig) would
  // otherwise fire while the `targets` field array is still empty and append a
  // blank config that clobbers the hydrated exposures (and zeroes the image
  // count, which then fails the exposureCount min:1 validation and silently
  // blocks save). Mounting the steps one render later — after the first `values`
  // application — lets their field arrays initialize already-populated.
  const [formReady, setFormReady] = useState(false);

  // RHF setup. Use `values` (reactive) instead of `defaultValues` (initial-only)
  // so that the form state syncs whenever the loaded observation changes.
  // ConstraintsStep's inner Controllers have their own `defaultValue` props
  // which can override one-shot reset() calls when they mount conditionally —
  // `values` propagates through those reliably.
  //
  // resetOptions.keepDirtyValues: critical when combined with `values`. Without
  // it, useFieldArray operations (append/remove) and any user input can be
  // wiped on subsequent renders because RHF re-syncs the form to `values`.
  // keepDirtyValues preserves anything the user (or append/remove) touched.
  const hydratedDefaults = useObservationFormDefaults(observation);
  const methods = useForm({
    defaultValues: useObservationFormDefaults(null),
    values: hydratedDefaults,
    resetOptions: { keepDirtyValues: true },
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        // Auxiliary lookups isolated with .catch so a broken /projects endpoint
        // doesn't block the actual observation load. Their failures log to the
        // console but don't surface as an error to the user.
        const [obsRes, observatoriesRes, projectsRes] = await Promise.all([
          apiService.getObservation(id),
          apiService.getObservatories().catch((err) => {
            console.error('[ObservationEdit] getObservatories failed:', err);
            return { data: { observatories: [] } };
          }),
          // getAllProjects walks every page so a project that hasn't been edited
          // recently isn't dropped from the change-project dropdown by the
          // server's default 20-item, updated_at-sorted first page.
          apiService.getAllProjects({ status: 'active' }).catch((err) => {
            console.error('[ObservationEdit] getAllProjects failed:', err);
            return [];
          }),
        ]);
        if (cancelled) return;

        const obs = obsRes.data;
        if (!isObservationEditable(obs.status)) {
          setError(`Cannot edit observation with status: ${obs.status}`);
          setLoading(false);
          return;
        }
        setObservation(obs);

        const obsyList = observatoriesRes?.data?.observatories
          ?? observatoriesRes?.data
          ?? [];
        setObservatories(Array.isArray(obsyList) ? obsyList : []);

        // Project list: filter to those the user can use/edit (matches ProjectStep
        // in ObservationSubmit). Then ensure the observation's currently-assigned
        // project is always in the list — even if it's archived or the user lost
        // edit permission, we still need to display its name in the dropdown so
        // the form shows the current value instead of going blank.
        // getAllProjects resolves to a flat array (or [] from its catch).
        const projList = Array.isArray(projectsRes) ? projectsRes : [];
        const usable = projList.filter(p => p.can_use || p.can_edit || p.is_owner);
        if (obs.project_id) {
          const currentId = typeof obs.project_id === 'object' ? obs.project_id._id : obs.project_id;
          const alreadyInList = usable.some(p => p._id === currentId);
          if (!alreadyInList) {
            // Try the unfiltered list, then the subdoc the observation payload
            // already carries (getObservation populates project_id with
            // name/code/type/status). Only fall back to GET /projects/:id when
            // neither has it — that endpoint requires project membership, and
            // its 403 is audited into the user's activity history.
            const fromFull = projList.find(p => p._id === currentId);
            const populated = typeof obs.project_id === 'object' ? obs.project_id : null;
            if (fromFull) {
              usable.unshift(fromFull);
            } else if (populated?.name) {
              usable.unshift(populated);
            } else {
              try {
                const single = await apiService.getProject(currentId);
                const project = single?.data?.project ?? single?.data ?? null;
                if (project) usable.unshift(project);
              } catch (err) {
                console.warn('[ObservationEdit] Could not load current project:', err);
              }
            }
          }
        }
        setProjects(usable);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load observation:', err);
          setError('Failed to load observation');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  // No manual reset needed — RHF's `values` prop above keeps form state in
  // sync with the loaded observation. buildObservationFormDefaults import
  // remains in case future logic needs it.

  // Flip formReady the render AFTER the observation lands. By then RHF's
  // internal `values` effect (registered at the useForm call above, so it runs
  // before this one) has hydrated the field arrays, so the step components can
  // safely mount without their mount effects clobbering the loaded exposures.
  useEffect(() => {
    if (observation) setFormReady(true);
  }, [observation]);

  const onSave = useObservationSubmit({
    mode: 'edit',
    observationId: id,
    loadedObservation: observation,
  });

  // #1119: live dark-time advisory while the window is being edited. Must sit above the early
  // returns below — it is a hook, and hooks cannot be called conditionally.
  const watchedType = methods.watch('observationType');
  const liveDarkTimeWarning = useDarkTimeWarning({
    observatoryId: methods.watch('assignedTo') || observation?.assigned_to,
    fixedTimeStart: methods.watch('fixedTimeStart'),
    fixedTimeEnd: methods.watch('fixedTimeEnd'),
    enabled: watchedType === 'fixed_time',
  });

  const handleSubmit = methods.handleSubmit(
    async (values) => {
      try {
        setSaving(true);
        setError(null);
        setDarkTimeWarnings([]);
        const result = await onSave(values);
        setSuccess(true);

        // #1119: the edit succeeded either way, but if the server flagged the window as running
        // past dark time, HOLD navigation. Auto-navigating 1.5s later would flash the advisory off
        // the screen before it could be read — the same invisibility that let EV Lac image into
        // twilight in the first place.
        const messages = warningMessages(result);
        if (messages.length > 0) {
          setDarkTimeWarnings(messages);
          setSaving(false);
          return;
        }
        setTimeout(() => navigate(`/observations/${id}`), 1500);
      } catch (err) {
        console.error('Failed to save observation:', err);
        setError(err.response?.data?.error?.message
          || err.response?.data?.error
          || err.message
          || 'Failed to save observation');
        setSaving(false);
      }
    },
    // onInvalid: without this branch, react-hook-form silently swallows the
    // click when ANY field (including collapsed/off-screen steps the user never
    // touched) fails validation — no PUT, no message, the Save button just
    // appears dead. Surface the offending fields so the failure is actionable
    // instead of invisible.
    (validationErrors) => {
      const fields = Object.keys(validationErrors);
      const detail = fields
        .map((name) => validationErrors[name]?.message || name)
        .filter(Boolean)
        .join('; ');
      console.warn('Observation edit blocked by client-side validation:', validationErrors);
      setError(
        `Cannot save — please fix the following before saving: ${detail || fields.join(', ')}`
      );
      setSaving(false);
    }
  );

  // Spinner while loading, or during the one-render gap before formReady (but
  // not when a fatal load error needs to surface below).
  if (loading || (!formReady && !error)) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !observation) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/observations')}>
          Back to Observations
        </Button>
      </Container>
    );
  }

  // Find the assigned observatory (for AutofocusStep and GuidingStep).
  const assignedTo = methods.watch('assignedTo');
  const assignedObservatory = observatories.find(o => o._id === assignedTo)
    || observatories.find(o => o._id === observation?.assigned_to)
    || observation?.assigned_to_full
    || null;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/observations')}>
                Back
              </Button>
              <Typography variant="h5">Edit Observation</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button startIcon={<CancelIcon />} onClick={() => navigate('/observations')}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving || success}
              >
                {saving ? 'Saving...' : success ? 'Saved!' : 'Save Changes'}
              </Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>Observation updated successfully</Alert>}

          {/* #1119: after save, the server's advisory holds navigation until acknowledged. */}
          <DarkTimeWarningAlert
            messages={darkTimeWarnings}
            onContinue={() => navigate(`/observations/${id}`)}
            continueLabel="Got it — view observation"
          />
          {/* Live, while editing: only when the save advisory is not already showing. */}
          {darkTimeWarnings.length === 0 && (
            <DarkTimeWarningAlert messages={liveDarkTimeWarning ? [liveDarkTimeWarning] : []} />
          )}

          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Observation Name" {...methods.register('observationName')} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Controller
                        name="priority"
                        control={methods.control}
                        render={({ field }) => (
                          <Select label="Priority" {...field}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(p => (
                              <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Observatory</InputLabel>
                      <Controller
                        name="assignedTo"
                        control={methods.control}
                        render={({ field }) => (
                          <Select label="Observatory" {...field} value={field.value ?? ''}>
                            {observatories.map(o => (
                              <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Project</InputLabel>
                      <Controller
                        name="projectId"
                        control={methods.control}
                        render={({ field }) => (
                          <Select label="Project" {...field} value={field.value ?? ''}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            {projects.map(p => (
                              <MenuItem key={p._id} value={p._id}>
                                {p.name}{p.code ? ` (${p.code})` : ''}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth multiline rows={2}
                      label="Description"
                      {...methods.register('description')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth multiline rows={2}
                      label="Notes"
                      placeholder="Any special instructions or context"
                      {...methods.register('notes')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="isFastMover"
                      control={methods.control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value === true}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label="Fast mover (asteroid / comet — bypass cadence checks)"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Targets + Exposures (Stage 4 — shared multi-target editor).
                Includes per-target RA/Dec, priority, exposure configs (with
                per-target sequence_repeat_count), and the global repeat count
                at the bottom. */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <UnifiedTargetStep
                  control={methods.control}
                  errors={methods.formState.errors}
                  observatoryLocation={assignedObservatory?.location}
                  // TargetExposureConfig expects [{value, label, color}] objects,
                  // not raw filter-name strings — same transform Submit uses.
                  availableFilters={createFilterOptions(
                    assignedObservatory?.capabilities?.available_filters
                  )}
                  availableReadoutModes={
                    assignedObservatory?.capabilities?.available_readout_modes
                    || assignedObservatory?.equipment_status?.camera?.readout_modes
                  }
                  // Per-camera default readout (pre-fill for NEW exposures
                  // added to existing targets; existing exposures keep their
                  // own readoutMode from the loaded document).
                  defaultReadoutMode={(() => {
                    const cam = assignedObservatory?.equipment_configuration?.camera;
                    const key = cam?.name
                      ? `${cam.name}_${cam.sensor_width_px ?? ''}_${cam.sensor_height_px ?? ''}`
                      : null;
                    const configs = assignedObservatory?.configuration?.calibration_settings?.camera_configs || {};
                    return (key && configs[key]?.default_readout_mode) || null;
                  })()}
                />
              </Paper>
            </Grid>

            {/* Constraints — drop-in shared component */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <ConstraintsStep
                  control={methods.control}
                  errors={methods.formState.errors}
                  watch={methods.watch}
                  setValue={methods.setValue}
                />
              </Paper>
            </Grid>

            {/* Transit baselines (only for time-based transits) */}
            {observation?.observation_type === 'time_based'
              && observation?.metadata?.observation_type === 'exoplanet_transit' && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Transit Baseline Times</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Adjusts the observation window relative to the FIXED transit ingress/egress times.
                    Start/end times are recalculated on save.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="constraints.baselineBefore"
                        control={methods.control}
                        render={({ field }) => (
                          <TextField
                            fullWidth type="number" label="Baseline Before Transit (minutes)"
                            {...field}
                            onChange={(e) => field.onChange(Math.max(0, Math.min(180, parseInt(e.target.value, 10) || 0)))}
                            inputProps={{ min: 0, max: 180, step: 5 }}
                            helperText="Observation time before ingress (0-180 min)"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="constraints.baselineAfter"
                        control={methods.control}
                        render={({ field }) => (
                          <TextField
                            fullWidth type="number" label="Baseline After Transit (minutes)"
                            {...field}
                            onChange={(e) => field.onChange(Math.max(0, Math.min(180, parseInt(e.target.value, 10) || 0)))}
                            inputProps={{ min: 0, max: 180, step: 5 }}
                            helperText="Observation time after egress (0-180 min)"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Guiding — drop-in shared component */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <GuidingStep observatories={observatories} />
              </Paper>
            </Grid>

            {/* Autofocus — drop-in shared component */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <AutofocusStep observatoryDefaults={assignedObservatory?.configuration} />
              </Paper>
            </Grid>

            {/* Repetition — drop-in shared component (#707 Stage 3). When
                isRepetitive is true, save uses the dedicated
                PUT /observations/:id/repetition-config endpoint. */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <RepetitionStep />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </Container>
  );
};

export default ObservationEdit;
